import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, PencilSimple, SignOut, MapPin, ArrowRight,
  Trophy, GameController, ChartLine, Medal, Star, Lightning, Robot, Handshake, ListChecks, CreditCard, Calendar, Microphone, Crown, Fire, BookOpen, Storefront,
  Users, UsersThree, Bell, CalendarDots, UserPlus, Clock, PaperPlaneTilt, Heart, Backpack, GraduationCap, ShoppingBag,
  Flag, UserMinus, Trash
} from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import BottomNav from '../components/BottomNav';
import { NotificationBell } from '../components/NotificationBell';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';
import { useEntitlement, useSubscription, useSmartNudges } from '../hooks/useEntitlement';
import { UpgradeChip, SmartNudgeStack } from '../components/UpgradePrompts';
import { SupportFooter } from '../components/Support';
import { UserBadgeList } from '../components/UserBadge';
import ReportModal from '../components/ReportModal';
import DeleteAccountModal from '../components/DeleteAccountModal';
import { clearAiConsent } from '../components/AiConsentModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLAY_STYLE_LABELS = {
  competitive: { label: 'Competitive', color: 'text-red-400 bg-red-400/10' },
  casual: { label: 'Casual', color: 'text-green-400 bg-green-400/10' },
  teaching: { label: 'Teacher', color: 'text-blue-400 bg-blue-400/10' },
  learning: { label: 'Learning', color: 'text-yellow-400 bg-yellow-400/10' },
  social: { label: 'Social', color: 'text-purple-400 bg-purple-400/10' }
};

export default function ProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [shopBadges, setShopBadges] = useState([]);
  const [friendStatus, setFriendStatus] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [collectionProfile, setCollectionProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [aiConsent, setAiConsent] = useState(() => {
    try { return localStorage.getItem('cyb_ai_consent') === 'granted'; } catch { return false; }
  });

  const toggleAiConsent = () => {
    if (aiConsent) {
      clearAiConsent();
      setAiConsent(false);
      try { axios.post(`${API_URL}/api/users/me/ai-consent`, { granted: false }, { withCredentials: true }).catch(() => {}); } catch { /* ignore */ }
      toast.success('AI features disabled. You can re-enable anytime.');
    } else {
      try { localStorage.setItem('cyb_ai_consent', 'granted'); } catch { /* ignore */ }
      setAiConsent(true);
      try { axios.post(`${API_URL}/api/users/me/ai-consent`, { granted: true }, { withCredentials: true }).catch(() => {}); } catch { /* ignore */ }
      toast.success('AI features enabled.');
    }
  };

  const isOwnProfile = !userId || userId === user?.id;

  const handleBlockUser = async () => {
    if (!profile?.id) return;
    if (!window.confirm(`Block ${profile.name}? They won't be matched with you or be able to message you.`)) return;
    try {
      await axios.post(
        `${API_URL}/api/matchmaking/block`,
        { blocked_user_id: profile.id, reason: 'user_initiated' },
        { withCredentials: true }
      );
      toast.success(`${profile.name} has been blocked`);
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not block user');
    }
  };

  useEffect(() => {
    fetchProfile();
  }, [userId, user?.id]);

  const fetchProfile = async () => {
    try {
      const profileId = isOwnProfile ? user?.id : userId;
      
      const [profileRes, statsRes, badgesRes] = await Promise.all([
        isOwnProfile 
          ? axios.get(`${API_URL}/api/users/profile`, { withCredentials: true })
          : axios.get(`${API_URL}/api/users/${userId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users/${profileId}/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/users/${profileId}/shop-badges`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      
      setProfile(profileRes.data);
      setStats(statsRes.data);
      setShopBadges(Array.isArray(badgesRes.data) ? badgesRes.data : []);

      // Fetch collection profile
      try {
        const colRes = isOwnProfile
          ? await axios.get(`${API_URL}/api/collections/stats/me`, { withCredentials: true })
          : await axios.get(`${API_URL}/api/collections/profile/${profileId}`, { withCredentials: true });
        setCollectionProfile(colRes.data);
      } catch (e) { /* silent */ }

      // Fetch friend status for other profiles
      if (!isOwnProfile) {
        try {
          const { data: fs } = await axios.get(`${API_URL}/api/friends/check/${userId}`, { withCredentials: true });
          setFriendStatus(fs);
        } catch (e) { /* silent */ }
      } else {
        // Fetch unread notification count for own profile
        try {
          const { data: nc } = await axios.get(`${API_URL}/api/notifications/unread-count`, { withCredentials: true });
          setUnreadCount(nc.count);
        } catch (e) { /* silent */ }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const playStyleInfo = profile?.play_style ? PLAY_STYLE_LABELS[profile.play_style] : null;
  const xpProgress = stats ? ((stats.xp % (stats.xp_for_next_level - (stats.level > 0 ? (stats.level * stats.level * 50) : 0))) / (stats.xp_for_next_level - (stats.level > 0 ? (stats.level * stats.level * 50) : 50))) * 100 : 0;
  const unlockedAchievements = stats?.achievements?.filter(a => a.unlocked)?.length || 0;
  const totalAchievements = stats?.achievements?.length || 0;
  const sub = useSubscription();
  const premiumFlair = useEntitlement('profile.premium_flair');
  const { nudges, dismiss: dismissNudge } = useSmartNudges();

  if (loading) {
    return (
      <div className="app-container flex items-center justify-center min-h-screen">
        <div className="w-10 h-10 border-4 border-[#9D4CDD] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="app-container flex flex-col items-center justify-center min-h-screen p-6">
        <p className="text-[#71717A]">Profile not found</p>
        <button onClick={() => navigate('/')} className="btn-primary mt-4">Go Home</button>
      </div>
    );
  }

  return (
    <div className="app-container" data-testid="profile-page">
      {/* Header */}
      <header className="p-6 pb-4 flex items-center justify-between">
        {!isOwnProfile ? (
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="back-button">
            <ArrowLeft size={20} weight="bold" className="text-white" />
          </button>
        ) : <div className="w-10"></div>}
        
        <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>Profile</h1>

        {isOwnProfile ? (
          <div className="flex items-center gap-1">
            <NotificationBell />
            <button onClick={() => navigate('/profile/edit')} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center" data-testid="edit-profile-button">
              <PencilSimple size={20} weight="duotone" className="text-[#00F0FF]" />
            </button>
          </div>
        ) : <div className="w-10"></div>}
      </header>

      {/* Profile Content */}
      <div className="px-6 pb-32">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col items-center">
          {/* Avatar */}
          {profile.avatar ? (
            <img src={profile.avatar} alt={profile.name} className="w-24 h-24 rounded-full object-cover border-4 border-[#9D4CDD]/30" />
          ) : (
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-[#9D4CDD] to-[#00F0FF] flex items-center justify-center text-2xl font-bold text-white border-4 border-[#9D4CDD]/30">
              {getInitials(profile.name)}
            </div>
          )}

          {/* Level Badge */}
          <div className="relative -mt-4">
            <div className="px-4 py-1 rounded-full bg-gradient-to-r from-[#9D4CDD] to-[#7C3AED] text-white text-sm font-bold flex items-center gap-1">
              <Star size={14} weight="fill" className="text-[#FACC15]" />
              Level {stats?.level || 0}
            </div>
          </div>

          {/* Name & Username */}
          <h2 className="text-2xl font-bold text-white mt-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>{profile.name}</h2>
          {profile.username && <p className="text-[#00F0FF] text-sm">@{profile.username}</p>}

          {/* Founder / Founding-Member badge — large, prominent */}
          {profile.badges?.length > 0 && (
            <div className="mt-3" data-testid="profile-founder-badge">
              <UserBadgeList badges={profile.badges} size="lg" showLabel={true} />
            </div>
          )}
          
          {profile.location && (
            <p className="text-[#A1A1AA] flex items-center gap-1 mt-1">
              <MapPin size={16} weight="duotone" className="text-[#9D4CDD]" />
              {profile.location}
            </p>
          )}

          {/* Play Style Badge */}
          {playStyleInfo && (
            <span className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${playStyleInfo.color}`}>
              {playStyleInfo.label} Player
            </span>
          )}

          {/* Subscription Badge */}
          {isOwnProfile && !sub.loading && sub.plan_code !== 'player_free' && (
            <div className="mt-2 flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#F59E0B]/10 border border-[#F59E0B]/20">
              <Crown size={12} weight="fill" className="text-[#F59E0B]" />
              <span className="text-[11px] text-[#F59E0B] font-semibold">{sub.plan_name}</span>
            </div>
          )}
          {isOwnProfile && !sub.loading && sub.plan_code === 'player_free' && (
            <div className="mt-2" data-testid="profile-upgrade-hint">
              <UpgradeChip plan="Plus" label="Upgrade Plan" />
            </div>
          )}

          {/* Smart Nudges for own profile */}
          {isOwnProfile && nudges.length > 0 && (
            <div className="w-full mt-4">
              <SmartNudgeStack nudges={nudges} maxShow={1} onDismiss={dismissNudge} />
            </div>
          )}

          {/* Bio */}
          {profile.bio && (
            <p className="text-center text-[#A1A1AA] mt-4 max-w-xs leading-relaxed">{profile.bio}</p>
          )}

          {/* XP Progress Bar */}
          <div className="w-full mt-6 p-4 rounded-2xl bg-[#0F0F16] border border-white/5">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[#A1A1AA] text-sm flex items-center gap-1">
                <Lightning size={14} weight="fill" className="text-[#FACC15]" />
                Experience
              </span>
              <span className="text-white text-sm font-medium">{stats?.xp || 0} XP</span>
            </div>
            <div className="h-2 bg-[#181824] rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.min(xpProgress, 100)}%` }}
                transition={{ duration: 1 }}
                className="h-full bg-gradient-to-r from-[#9D4CDD] to-[#00F0FF] rounded-full"
              />
            </div>
            <p className="text-[#71717A] text-xs mt-1 text-right">
              {stats?.xp_for_next_level - stats?.xp} XP to Level {(stats?.level || 0) + 1}
            </p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-4 gap-2 w-full mt-4">
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 text-center">
              <GameController size={18} weight="duotone" className="text-[#9D4CDD] mx-auto mb-1" />
              <p className="text-white font-bold">{stats?.total_games || 0}</p>
              <p className="text-[#71717A] text-[10px]">Games</p>
            </div>
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 text-center">
              <Trophy size={18} weight="duotone" className="text-[#39FF14] mx-auto mb-1" />
              <p className="text-white font-bold">{stats?.wins || 0}</p>
              <p className="text-[#71717A] text-[10px]">Wins</p>
            </div>
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 text-center">
              <ChartLine size={18} weight="duotone" className="text-[#00F0FF] mx-auto mb-1" />
              <p className="text-white font-bold">{stats?.win_rate || 0}%</p>
              <p className="text-[#71717A] text-[10px]">Win Rate</p>
            </div>
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 text-center">
              <Medal size={18} weight="duotone" className="text-[#FACC15] mx-auto mb-1" />
              <p className="text-white font-bold">{unlockedAchievements}/{totalAchievements}</p>
              <p className="text-[#71717A] text-[10px]">Badges</p>
            </div>
          </div>

          {/* Streaks & Tournaments */}
          <div className="grid grid-cols-2 gap-2 w-full mt-2">
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#EF4444]/20 flex items-center justify-center">
                <Lightning size={20} weight="fill" className="text-[#EF4444]" />
              </div>
              <div>
                <p className="text-white font-bold">{stats?.current_win_streak || 0}</p>
                <p className="text-[#71717A] text-[10px]">Win Streak</p>
              </div>
            </div>
            <div className="p-3 rounded-2xl bg-[#0F0F16] border border-white/5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#FFD700]/20 flex items-center justify-center">
                <Trophy size={20} weight="fill" className="text-[#FFD700]" />
              </div>
              <div>
                <p className="text-white font-bold">{stats?.tournaments_won || 0}</p>
                <p className="text-[#71717A] text-[10px]">Tournaments Won</p>
              </div>
            </div>
          </div>

          {/* Game Ratings */}
          {stats?.ratings && stats.ratings.length > 0 && (
            <div className="w-full mt-6">
              <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Game Ratings</h3>
              <div className="space-y-2">
                {stats.ratings.map((rating) => (
                  <div key={rating.game_type} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <GamePill game={rating.game_type} small />
                      <span className="text-[#71717A] text-xs">{rating.games_played} games</span>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-bold">{rating.rating}</p>
                      <p className="text-[#71717A] text-[10px]">ELO</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preferred Games */}
          {profile.preferred_games && Array.isArray(profile.preferred_games) && profile.preferred_games.length > 0 && (
            <div className="w-full mt-6">
              <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3">Favorite Games</h3>
              <div className="flex flex-wrap gap-2">
                {profile.preferred_games.map((game) => (
                  <GamePill key={game} game={game} />
                ))}
              </div>
            </div>
          )}

          {/* Shop Badges */}
          {shopBadges.length > 0 && (
            <div className="w-full mt-6" data-testid="shop-badges-section">
              <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
                <Storefront size={14} weight="duotone" className="text-[#00F0FF]" />
                Shop Badges
              </h3>
              <div className="flex flex-wrap gap-2">
                {shopBadges.map((badge, i) => (
                  <motion.div key={i} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.05 }}
                    className="px-3 py-2 rounded-xl bg-[#FACC15]/5 border border-[#FACC15]/20 flex items-center gap-2"
                    data-testid={`shop-badge-${i}`}>
                    <Trophy size={14} weight="fill" className="text-[#FACC15]" />
                    <div>
                      <p className="text-[#FACC15] text-[10px] font-medium">{badge.badge_name}</p>
                      <p className="text-[#71717A] text-[9px]">{badge.shop_name}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Game Collection Identity */}
          {collectionProfile && (isOwnProfile ? collectionProfile.total > 0 : collectionProfile.owned_count > 0) && (
            <div className="w-full mt-6" data-testid="collection-identity-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-[#A1A1AA] flex items-center gap-2">
                  <GameController size={14} weight="duotone" className="text-[#10B981]" />
                  Game Collection
                </h3>
                {isOwnProfile && (
                  <button onClick={() => navigate('/collection')} className="text-[10px] text-[#10B981] hover:text-[#10B981]/80 font-medium" data-testid="view-collection-link">
                    View All <ArrowRight size={10} className="inline" />
                  </button>
                )}
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-2 mb-3">
                <div className="p-2 rounded-xl bg-[#10B981]/5 border border-[#10B981]/10 text-center">
                  <p className="text-white font-bold text-sm">{isOwnProfile ? collectionProfile.owned : collectionProfile.owned_count}</p>
                  <p className="text-[#10B981] text-[9px]">Owned</p>
                </div>
                <div className="p-2 rounded-xl bg-[#F59E0B]/5 border border-[#F59E0B]/10 text-center">
                  <p className="text-white font-bold text-sm">{isOwnProfile ? collectionProfile.favorites : (collectionProfile.favorite_games?.length || 0)}</p>
                  <p className="text-[#F59E0B] text-[9px]">Favorites</p>
                </div>
                <div className="p-2 rounded-xl bg-[#9D4CDD]/5 border border-[#9D4CDD]/10 text-center">
                  <p className="text-white font-bold text-sm">{isOwnProfile ? collectionProfile.wishlist : collectionProfile.wishlist_count}</p>
                  <p className="text-[#9D4CDD] text-[9px]">Wishlist</p>
                </div>
                {isOwnProfile && (
                  <div className="p-2 rounded-xl bg-[#3B82F6]/5 border border-[#3B82F6]/10 text-center">
                    <p className="text-white font-bold text-sm">{collectionProfile.willing_to_bring}</p>
                    <p className="text-[#3B82F6] text-[9px]">Can Bring</p>
                  </div>
                )}
                {!isOwnProfile && (
                  <div className="p-2 rounded-xl bg-[#06B6D4]/5 border border-[#06B6D4]/10 text-center">
                    <p className="text-white font-bold text-sm">{collectionProfile.played_count}</p>
                    <p className="text-[#06B6D4] text-[9px]">Played</p>
                  </div>
                )}
              </div>

              {/* Favorite games */}
              {!isOwnProfile && collectionProfile.favorite_games?.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {collectionProfile.favorite_games.map(g => (
                    <span key={g.game_id} className="px-2.5 py-1 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[10px] font-medium flex items-center gap-1">
                      <Heart size={10} weight="fill" /> {g.game_name}
                    </span>
                  ))}
                </div>
              )}

              {/* Own profile: teach/bring badges */}
              {isOwnProfile && collectionProfile.willing_to_teach > 0 && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="px-2 py-0.5 rounded-full bg-[#F59E0B]/10 text-[#F59E0B] text-[9px] font-medium flex items-center gap-1">
                    <GraduationCap size={10} /> Can teach {collectionProfile.willing_to_teach} game{collectionProfile.willing_to_teach > 1 ? 's' : ''}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Quick Links - Compact for own profile */}
          {isOwnProfile && (
            <div className="w-full mt-6">
              <div className="grid grid-cols-2 gap-2">
                <button onClick={() => navigate('/collection')} className="p-3 rounded-xl bg-gradient-to-r from-[#10B981]/10 to-[#10B981]/5 border border-[#10B981]/20 flex items-center gap-3 text-left hover:border-[#10B981]/40 transition-all" data-testid="profile-collection-link">
                  <GameController size={18} weight="duotone" className="text-[#10B981]" />
                  <span className="text-white text-sm">Collection</span>
                </button>
                <button onClick={() => navigate('/friends')} className="p-3 rounded-xl bg-[#0F0F16] border border-[#9D4CDD]/20 flex items-center gap-2 text-left hover:border-[#9D4CDD]/40 transition-all" data-testid="profile-friends-link">
                  <Users size={18} weight="duotone" className="text-[#9D4CDD]" />
                  <span className="text-white text-sm">Friends</span>
                </button>
                <button onClick={() => navigate('/notifications/saved-alerts')} className="p-3 rounded-xl bg-[#0F0F16] border border-[#39FF14]/15 flex items-center gap-2 text-left hover:border-[#39FF14]/30 transition-all" data-testid="profile-saved-alerts-link">
                  <Star size={20} weight="duotone" className="text-[#39FF14]" />
                  <span className="text-white text-sm">Saved Alerts</span>
                </button>
                <button onClick={() => navigate('/profile/edit')} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5 flex items-center gap-2 text-left hover:border-[#00F0FF]/30 transition-all" data-testid="profile-edit-link">
                  <PencilSimple size={18} weight="duotone" className="text-[#00F0FF]" />
                  <span className="text-white text-sm">Edit Profile</span>
                </button>
              </div>
            </div>
          )}

          {/* Recent Activity (Social Posts) */}
          <RecentActivity userId={isOwnProfile ? user?.id : userId} navigate={navigate} />

          {/* Actions */}
          {!isOwnProfile ? (
            <div className="w-full mt-8 space-y-3">
              {/* Friend Action */}
              {friendStatus && friendStatus.is_friend ? (
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/chat/${profile.id}`)} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="message-user-button">
                    <PaperPlaneTilt size={18} weight="fill" /> Message
                  </button>
                  <div className="px-4 py-3 rounded-xl bg-[#39FF14]/10 flex items-center gap-2" data-testid="friend-status-badge">
                    <Users size={16} weight="fill" className="text-[#39FF14]" />
                    <span className="text-[#39FF14] text-sm font-medium">Friends</span>
                  </div>
                </div>
              ) : friendStatus && friendStatus.pending_request ? (
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/chat/${profile.id}`)} className="btn-primary flex-1 flex items-center justify-center gap-2" data-testid="message-user-button">
                    <PaperPlaneTilt size={18} weight="fill" /> Message
                  </button>
                  <div className="px-4 py-3 rounded-xl bg-[#FACC15]/10 flex items-center gap-2" data-testid="pending-status-badge">
                    <Clock size={16} weight="fill" className="text-[#FACC15]" />
                    <span className="text-[#FACC15] text-sm font-medium">Pending</span>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => navigate(`/chat/${profile.id}`)} className="flex-1 py-3 rounded-xl bg-[#181824] border border-white/10 text-white text-sm font-medium flex items-center justify-center gap-2" data-testid="message-user-button">
                    <PaperPlaneTilt size={18} weight="fill" /> Message
                  </button>
                  <button onClick={async () => {
                    try {
                      const { data } = await axios.post(`${API_URL}/api/friends/request`, { to_user_id: profile.id }, { withCredentials: true });
                      if (data.status === 'accepted') {
                        setFriendStatus({ is_friend: true, pending_request: false });
                      } else {
                        setFriendStatus({ is_friend: false, pending_request: true, request_direction: 'sent' });
                      }
                      import('sonner').then(m => m.toast.success(data.message));
                    } catch (err) {
                      import('sonner').then(m => m.toast.error(err.response?.data?.detail || 'Failed'));
                    }
                  }} className="flex-1 py-3 rounded-xl bg-gradient-to-r from-[#9D4CDD] to-[#7C3AED] text-white text-sm font-bold flex items-center justify-center gap-2" data-testid="add-friend-button">
                    <UserPlus size={18} weight="bold" /> Add Friend
                  </button>
                </div>
              )}

              {/* Report / Block (Apple App Store Guideline 1.2 compliance) */}
              <div className="mt-4 flex items-center justify-center gap-4 pt-3 border-t border-white/[0.04]">
                <button
                  onClick={() => setShowReportModal(true)}
                  className="flex items-center gap-1.5 text-[#71717A] hover:text-[#FACC15] text-xs transition-colors"
                  data-testid="report-user-btn"
                >
                  <Flag size={14} weight="duotone" /> Report user
                </button>
                <span className="text-[#333] text-xs">·</span>
                <button
                  onClick={handleBlockUser}
                  className="flex items-center gap-1.5 text-[#71717A] hover:text-red-400 text-xs transition-colors"
                  data-testid="block-user-btn"
                >
                  <UserMinus size={14} weight="duotone" /> Block
                </button>
              </div>
            </div>
          ) : (
            <>
              <button onClick={handleLogout} className="mt-8 flex items-center gap-2 text-red-400 hover:text-red-300 transition-colors" data-testid="logout-button">
                <SignOut size={20} weight="duotone" />
                Sign Out
              </button>
              <div className="mt-6 pt-4 border-t border-white/[0.04]">
                <SupportFooter />
              </div>
              {/* Native-only quick-reset is now gone; admin can re-trigger via
                  Admin Console → Users → {user} → "Reset onboarding". Keeping
                  the consent toggle and delete account below. */}

              {/* Build info — show the user (and Apple's reviewer) exactly which
                  bundle the phone is running. Tap → /build-info. */}
              <div className="mt-4 pt-4 border-t border-white/[0.04]">
                <button
                  onClick={() => navigate('/build-info')}
                  className="w-full flex items-center justify-between py-2 text-[12px] text-[#888] hover:text-white transition-colors"
                  data-testid="profile-build-info-link"
                >
                  <span>Build info</span>
                  <span className="text-[10px] text-[#444] font-mono">{require('../buildInfo.json').version} · {require('../buildInfo.json').commit}</span>
                </button>
              </div>

              {/* Apple App Store Guideline 5.1.1(i) — AI Preferences (third-party AI consent) */}
              <div className="mt-4 pt-4 border-t border-white/[0.04]" data-testid="ai-preferences-section">
                <div className="flex items-center justify-between">
                  <div className="flex-1 pr-3">
                    <p className="text-white text-xs font-semibold flex items-center gap-1.5">
                      <Robot size={14} weight="duotone" className="text-[#00F0FF]" />
                      AI features (OpenAI GPT-4o)
                    </p>
                    <p className="text-[10px] text-[#666] mt-1 leading-snug">
                      Powers Rules, AI Assistant &amp; Board Scan. Sends your prompts and (for Scan) uploaded photos to OpenAI for processing.
                      <a href="/privacy" className="text-[#00C8D7] underline ml-1">Learn more</a>
                    </p>
                  </div>
                  <button
                    onClick={toggleAiConsent}
                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 p-0 ${aiConsent ? 'bg-[#9D4CDD]' : 'bg-[#2a2a36]'}`}
                    data-testid="ai-consent-toggle"
                    aria-label={aiConsent ? 'Disable AI features' : 'Enable AI features'}
                  >
                    {/* Track: 44px × 24px. Knob: 20px × 20px. Anchored top-0.5
                        + left-0.5 (2px inset from top/left). translate-x-[20px]
                        slides knob from x=2 → x=22, leaving exactly 2px gap
                        from the right edge in ON state. p-0 on button resets
                        browser default button padding which used to leak in
                        and break the geometry on iOS Safari/WKWebView. */}
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${aiConsent ? 'translate-x-[20px]' : 'translate-x-0'}`} />
                  </button>
                </div>
              </div>

              {/* Apple App Store Guideline 5.1.1(v) — in-app account deletion */}
              <div className="mt-4 pt-4 border-t border-white/[0.04]">
                <button
                  onClick={() => setShowDeleteAccount(true)}
                  className="flex items-center gap-2 text-[#71717A] hover:text-red-400 text-xs transition-colors"
                  data-testid="delete-account-btn"
                >
                  <Trash size={14} weight="duotone" /> Delete my account
                </button>
                <p className="text-[10px] text-[#444] mt-1.5">Permanently removes your profile, friends, and history. Cannot be undone.</p>
              </div>
            </>
          )}
        </motion.div>
      </div>

      {/* Modals */}
      <ReportModal
        open={showReportModal}
        onClose={() => setShowReportModal(false)}
        entityType="user"
        entityId={profile?.id || ''}
        entityLabel={profile ? `${profile.name} (@${profile.username || 'player'})` : ''}
      />
      <DeleteAccountModal open={showDeleteAccount} onClose={() => setShowDeleteAccount(false)} />

      {isOwnProfile && <BottomNav />}
    </div>
  );
}

// ── Recent Activity Section ──
function RecentActivity({ userId, navigate }) {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    if (!userId) return;
    axios.get(`${API_URL}/api/social/feed/user/${userId}`, { withCredentials: true })
      .then(r => setPosts((r.data || []).slice(0, 3)))
      .catch(() => {});
  }, [userId]);

  if (posts.length === 0) return null;

  const timeAgo = (iso) => {
    if (!iso) return '';
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h`;
    return `${Math.floor(hrs / 24)}d`;
  };

  return (
    <div className="w-full mt-6" data-testid="recent-activity-section">
      <h3 className="text-sm font-semibold text-[#A1A1AA] mb-3 flex items-center gap-2">
        <Fire size={14} weight="duotone" className="text-[#F97316]" />
        Recent Activity
      </h3>
      <div className="space-y-2">
        {posts.map(p => (
          <div key={p.id} className="p-3 rounded-xl bg-[#0F0F16] border border-white/5">
            <p className="text-white text-sm leading-relaxed line-clamp-3">{p.content}</p>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-[#555]">
              {p.game_name && <span className="text-[#9D4CDD]"><GameController size={10} className="inline" /> {p.game_name}</span>}
              <span>{timeAgo(p.created_at)}</span>
              <span><Heart size={10} className="inline" /> {p.likes_count}</span>
            </div>
          </div>
        ))}
      </div>
      <button onClick={() => navigate('/discover')} className="w-full mt-2 text-center text-[#9D4CDD] text-xs hover:underline py-2">
        View all activity
      </button>
    </div>
  );
}
